/**
 * DubiousItemsTab — tab "Dudosos" del panel Data Health.
 *
 * Lista productos visibles pero con señales sospechosas:
 *  - subcategory IS NULL
 *  - confidence_score < 0.7
 *  - validación V2 con flags (below_threshold, hallucination, etc.)
 *
 * v1 MVP:
 *  - Bulk Ocultar (con modal de motivo)
 *  - Bulk Re-catalogar (con modal confirm)
 *  - Drawer detalle
 *  - Otras bulk actions: stubbed v2.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, AlertTriangle, X } from 'lucide-react';
import type { DataHealthItem, DubiousReason, HiddenReason } from '../../../types/dataHealth';
import {
  bulkHide,
  errorMessage,
  isBackendUnavailable,
  listDubious,
  triggerRecatalog,
} from '../../../services/dataHealth';
import {
  ConfidenceBadge,
  FilterChip,
  formatDateTime,
} from '../shared/inspectorTableHelpers';
import BulkActionBar, { EyeOff, RefreshCw, Pencil, Download, Flag } from './BulkActionBar';
import DataHealthList, { type DataHealthColumn } from './DataHealthList';
import DataHealthRowDrawer from './DataHealthRowDrawer';
import HideReasonModal from './HideReasonModal';
import RecatalogConfirmModal from './RecatalogConfirmModal';

interface Props {
  clientId: string;
  /** Reservado para v2 (toggle "Incluir ocultos"). */
  isSuperadmin?: boolean;
  onToast: (msg: string) => void;
}

const PAGE_SIZE = 50;

const REASON_FILTERS: { key: DubiousReason | 'all'; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'subcat_null', label: 'Subcat null' },
  { key: 'low_confidence', label: 'Baja confianza' },
  { key: 'contenido_out_of_range', label: 'Contenido fuera' },
  { key: 'forced_to_otros', label: 'Forced OTROS' },
  { key: 'llm_error', label: 'LLM error' },
  // 'brand_not_whitelisted' removido: son brand suggestions del prompt, no dudosos
];

const DubiousItemsTab: React.FC<Props> = ({ clientId, onToast }) => {
  const [items, setItems] = useState<DataHealthItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [backendDown, setBackendDown] = useState(false);

  const [reason, setReason] = useState<DubiousReason | 'all'>('all');
  const [search, setSearch] = useState('');

  // Orden del servidor (DataHealthList → onSort). El backend refetchea con estos params.
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawerItem, setDrawerItem] = useState<DataHealthItem | null>(null);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [recatalogModalOpen, setRecatalogModalOpen] = useState(false);

  // Guard anti-race: solo la respuesta más nueva escribe estado (T-B).
  const reqIdRef = useRef(0);

  const loadData = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    setBackendDown(false);
    try {
      const r = await listDubious(clientId, {
        page,
        limit: PAGE_SIZE,
        dubiousReason: reason !== 'all' ? reason : undefined,
        search: search.trim() || undefined,
        sortBy: sortBy || undefined,
        sortDir: sortDir || undefined,
      });
      if (myReq !== reqIdRef.current) return; // respuesta stale
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      if (isBackendUnavailable(e)) {
        setBackendDown(true);
      } else {
        setErr(errorMessage(e));
      }
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [clientId, page, reason, search, sortBy, sortDir]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    setSelected(new Set());
    setPage(1);
  }, [reason, search]);

  // Cambio de orden → volver a página 1 (refetch lo dispara el cambio de sortBy/sortDir).
  const handleSort = useCallback((by: string, dir: 'asc' | 'desc') => {
    setSortBy(by);
    setSortDir(dir);
    setPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const pageAllSelected = useMemo(
    () => items.length > 0 && items.every((it) => selected.has(it.id_catalogo)),
    [items, selected]
  );

  const togglePage = (checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) items.forEach((it) => next.add(it.id_catalogo));
      else items.forEach((it) => next.delete(it.id_catalogo));
      return next;
    });
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectedItems = useMemo(
    () => items.filter((it) => selected.has(it.id_catalogo)),
    [items, selected]
  );

  const handleConfirmHide = async (chosenReason: HiddenReason, note: string) => {
    const ids = Array.from(selected);
    try {
      await bulkHide(clientId, ids, chosenReason, note || undefined);
      onToast(`Ocultados ${ids.length} producto(s)`);
      setSelected(new Set());
      void loadData();
    } catch (e) {
      if (isBackendUnavailable(e)) {
        throw new Error('Backend pendiente de deploy');
      }
      throw new Error(errorMessage(e));
    }
  };

  const handleConfirmRecatalog = async () => {
    const ids = Array.from(selected);
    try {
      const r = await triggerRecatalog(clientId, ids);
      onToast(`Re-cataloga lanzado (run ${r.runId ? r.runId.slice(0, 8) : 'n/a'})`);
      setSelected(new Set());
      void loadData();
    } catch (e) {
      if (isBackendUnavailable(e)) {
        throw new Error('Backend pendiente de deploy');
      }
      throw new Error(errorMessage(e));
    }
  };

  if (backendDown && !loading) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
        <div className="font-medium text-amber-800 mb-1">Backend pendiente de deploy</div>
        <div className="text-amber-700 text-xs">
          Los endpoints para listar items dudosos (`/catalog?dubious=1`) todavía no
          están disponibles. La UI está lista — esperando deploy del backend.
        </div>
      </div>
    );
  }

  // Config de columnas para DataHealthList. Anchos FIJOS (table-layout:fixed):
  // title es la columna flexible (sin width). Mismos headers/orden/renders que
  // la <table> ad-hoc anterior.
  const columns: DataHealthColumn<DataHealthItem>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: false,
      render: (it) => (
        <span className="block truncate" title={it.title || ''}>
          {it.title || <span className="text-gray-400">—</span>}
        </span>
      ),
    },
    {
      key: 'marca',
      header: 'Marca',
      width: '9rem',
      render: (it) => it.catalog_brand || it.marca || '—',
    },
    {
      key: 'subcat',
      header: 'Subcat',
      width: '9rem',
      render: (it) => it.subcategory || <span className="text-orange-600 italic">null</span>,
    },
    {
      key: 'segmento',
      header: 'Segmento',
      width: '9rem',
      render: (it) => it.segmento || '—',
    },
    {
      key: 'conf',
      header: 'Conf',
      width: '5rem',
      align: 'right',
      render: (it) => <ConfidenceBadge value={it.confidence_score} />,
    },
    {
      key: 'catalog_at',
      header: 'Catalog at',
      width: '9rem',
      sortable: false,
      cellClassName: 'tabular-nums',
      render: (it) => formatDateTime(it.last_catalog_at),
    },
    {
      key: 'sitio',
      header: 'Sitio',
      width: '8rem',
      sortable: false,
      render: (it) =>
        it.url ? (
          <a
            href={it.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800"
          >
            {it.sitio || '—'}
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        ) : (
          it.sitio || '—'
        ),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Filter chips + search */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex flex-wrap gap-1.5">
          {REASON_FILTERS.map((f) => (
            <FilterChip
              key={f.key}
              active={reason === f.key}
              label={f.label}
              onClick={() => setReason(f.key)}
            />
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título…"
            className="text-[11px] border border-gray-300 rounded px-2 py-1 w-56 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="p-0.5 text-gray-400 hover:text-gray-600"
              title="Limpiar"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Error banner (el vacío/carga los maneja DataHealthList) */}
      {err && (
        <div className="mb-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {err}
        </div>
      )}

      {/* Listado unificado (DataHealthList) con selección múltiple + drawer overlay */}
      <DataHealthList<DataHealthItem>
        columns={columns}
        rows={items}
        getRowId={(it) => it.id_catalogo}
        onRowClick={(it) => setDrawerItem(it)}
        activeRowId={drawerItem?.id_catalogo ?? null}
        listId="datahealth-dudosos"
        onSort={handleSort}
        sortBy={sortBy}
        sortDir={sortDir}
        loading={loading}
        emptyState={
          <div className="py-2 text-gray-500">
            <AlertTriangle className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            No hay productos dudosos en esta vista.
            <div className="text-[11px] mt-1 text-gray-400">
              El último batch fue limpio. Si querés bajar el umbral, ajustá el threshold en
              Catalogador IA → Prompts.
            </div>
          </div>
        }
        selection={{
          selected,
          pageAllSelected,
          onTogglePage: togglePage,
          onToggleOne: toggleOne,
        }}
        pagination={{
          idPrefix: 'dudosos',
          pageIndex: page - 1,
          pageSize: PAGE_SIZE,
          totalRows: total,
          totalPages,
          onPageChange: (newIndex) => setPage(newIndex + 1),
          onPageSizeChange: () => {},
        }}
        overlay={
          drawerItem ? (
            <DataHealthRowDrawer
              clientId={clientId}
              item={drawerItem}
              onClose={() => setDrawerItem(null)}
              onPrev={() => {
                const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
                if (i > 0) setDrawerItem(items[i - 1]);
              }}
              onNext={() => {
                const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
                if (i >= 0 && i < items.length - 1) setDrawerItem(items[i + 1]);
              }}
              hasPrev={items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo) > 0}
              hasNext={(() => {
                const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
                return i >= 0 && i < items.length - 1;
              })()}
            />
          ) : null
        }
      />

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selected.size}
        onClear={() => setSelected(new Set())}
        actions={[
          {
            id: 'hide',
            label: 'Ocultar',
            icon: EyeOff,
            variant: 'primary',
            onClick: () => setHideModalOpen(true),
          },
          {
            id: 'recatalog',
            label: 'Re-catalogar',
            icon: RefreshCw,
            variant: 'warning',
            onClick: () => setRecatalogModalOpen(true),
          },
          {
            id: 'edit-brand',
            label: 'Editar marca',
            icon: Pencil,
            variant: 'primary',
            onClick: () => {},
            disabled: true,
            disabledHint: 'Próximamente (v2)',
          },
          {
            id: 'export',
            label: 'Exportar CSV',
            icon: Download,
            variant: 'neutral',
            onClick: () => {},
            disabled: true,
            disabledHint: 'Próximamente (v2)',
          },
          {
            id: 'report',
            label: 'Reportar TomaDatos',
            icon: Flag,
            variant: 'neutral',
            onClick: () => {},
            disabled: true,
            disabledHint: 'Próximamente (v2)',
          },
        ]}
      />

      {/* Modals */}
      <HideReasonModal
        isOpen={hideModalOpen}
        selectedItems={selectedItems}
        onClose={() => setHideModalOpen(false)}
        onConfirm={handleConfirmHide}
      />
      <RecatalogConfirmModal
        isOpen={recatalogModalOpen}
        selectedItems={selectedItems}
        activePromptVersion={null}
        onClose={() => setRecatalogModalOpen(false)}
        onConfirm={handleConfirmRecatalog}
      />

    </div>
  );
};

export default DubiousItemsTab;
